interface BboxStatusIconProps {
  cx: number;
  cy: number;
  status: string;
  color: string;
  size: number;
  /**
   * Image aspect ratio (height/width). Used to counter-scale the icon so it
   * renders as a circle even when the SVG viewBox stretches non-uniformly.
   */
  aspectRatio?: number;
  minRadius?: number;
}

/**
 * Renders a circular status indicator (sold / reserved / cancelled) inside an
 * SVG bounding box. Shared across bbox overlay variants so the visual language
 * stays consistent.
 */
export function BboxStatusIcon({
  cx,
  cy,
  status,
  color,
  size,
  aspectRatio = 1,
  minRadius = 24,
}: BboxStatusIconProps) {
  if (status !== "sold" && status !== "reserved" && status !== "cancelled") {
    return null;
  }

  const r = Math.max(size / 2, minRadius);
  const iconSize = r * 0.55;
  const sx = aspectRatio <= 1 ? aspectRatio : 1;
  const sy = aspectRatio <= 1 ? 1 : 1 / aspectRatio;

  return (
    <g transform={`translate(${cx},${cy}) scale(${sx},${sy}) translate(${-cx},${-cy})`}>
      <circle cx={cx} cy={cy} r={r} fill="white" fillOpacity={0.85} />
      <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2} />

      {status === "sold" && (
        <polyline
          points={`${cx - iconSize * 0.6},${cy} ${cx - iconSize * 0.15},${cy + iconSize * 0.5} ${cx + iconSize * 0.6},${cy - iconSize * 0.4}`}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {status === "reserved" && (
        <>
          <circle cx={cx} cy={cy} r={iconSize * 0.7} fill="none" stroke={color} strokeWidth={2.5} />
          <line x1={cx} y1={cy - iconSize * 0.35} x2={cx} y2={cy} stroke={color} strokeWidth={2.5} strokeLinecap="round" />
          <line x1={cx} y1={cy} x2={cx + iconSize * 0.3} y2={cy + iconSize * 0.15} stroke={color} strokeWidth={2.5} strokeLinecap="round" />
        </>
      )}

      {status === "cancelled" && (
        <>
          <line x1={cx - iconSize * 0.4} y1={cy - iconSize * 0.4} x2={cx + iconSize * 0.4} y2={cy + iconSize * 0.4} stroke={color} strokeWidth={3} strokeLinecap="round" />
          <line x1={cx + iconSize * 0.4} y1={cy - iconSize * 0.4} x2={cx - iconSize * 0.4} y2={cy + iconSize * 0.4} stroke={color} strokeWidth={3} strokeLinecap="round" />
        </>
      )}
    </g>
  );
}
